<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import NewHeader from '~/components/NewHeader.vue';
import EditorSidebar from '~/components/editor/EditorSidebar.vue';
import EditorPanelFaceSwap from '~/components/editor/EditorPanelFaceSwap.vue';
import EditorPanelSexPose from '~/components/editor/EditorPanelSexPose.vue';
import EditorPanelAiVideo from '~/components/editor/EditorPanelAiVideo.vue';
import EditorPanelSeedance from '~/components/editor/EditorPanelSeedance.vue';
import SeedanceResultPanel from '~/components/editor/SeedanceResultPanel.vue';
import EditorUploadZone from '~/components/editor/EditorUploadZone.vue';
import EditorBridgeGenerate from '~/components/editor/EditorBridgeGenerate.vue';
import CreditsDialog from '~/components/CreditsDialog.vue';

definePageMeta({ layout: false });
const { t } = useI18n();
useHead({
  title: () => t('editor.workspace'),
  meta: [{ name: 'robots', content: 'noindex,nofollow' }],
});

type Tool = 'face-swap' | 'sex-pose' | 'ai-video' | 'seedance';

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();

const tool = computed<Tool>(() => {
  const t = route.query.type as string;
  if (t === 'sex-pose' || t === 'ai-video' || t === 'face-swap' || t === 'seedance') return t;
  return 'face-swap';
});

// 上传到工作台的素材
const sourceImage = ref('');
const sourceImages = ref<string[]>([]);
const targetMaterialId = ref('');
// 上传/裁剪/选素材带过来的图片比例，用来让 sex-pose 自动选中左侧 Ratio
const uploadedAspect = ref('');
const result = ref<{ url: string; type: 'image' | 'video' } | null>(null);
const polling = ref(false);
const isUploading = ref(false);

function handleUploading(value: boolean) {
  isUploading.value = value;
}

// 从 landing page 上传后带过来的图片
onMounted(() => {
  const img = route.query.img as string | undefined;
  if (img) {
    sourceImage.value = img;
    sourceImages.value = [img];
  }
});

function switchTool(t: Tool) {
  router.push({ path: localePath('/editor'), query: { type: t } });
  // 切换 tab 时不清空已上传素材，让用户可以试不同效果
  result.value = null;
}

function handleUpload(payload: { url: string; materialId: string; ratio?: string }) {
  sourceImage.value = payload.url;
  targetMaterialId.value = payload.materialId;
  if (sourceImages.value.length < 3) sourceImages.value.push(payload.url);
  if (payload.ratio) uploadedAspect.value = payload.ratio;
}

function handleResult(payload: { url: string; type: 'image' | 'video' }) {
  result.value = payload;
}

function handleReset() {
  result.value = null;
}

// 上传区下面/移动端底部的桥接生成按钮：三个 tool 通用。
// 同一时刻只挂载一个 panel，activePanel 指向当前 panel，
// panelState 由当前 panel 的 watchEffect 镜像过来，生成逻辑仍全在各 panel 内。
const activePanel = ref<any>(null);
const panelState = ref<{ canGenerate: boolean; label: string; costLabel: string; busy: boolean; mode?: string }>({
  canGenerate: false,
  label: '',
  costLabel: '',
  busy: false,
});

// Seedance 结果流 → 转发到 EditorPanelSeedance（示例回填 / 按订单参数回填）
function onUseSeedanceExample() {
  activePanel.value?.applyExample?.();
}
function onUseSeedanceVideo(video: any) {
  activePanel.value?.applyFromOrder?.(video);
}
// 右侧结果面板引用 —— 提交成功后刷新出「生成中」卡片（非阻塞流）
const seedanceResultRef = ref<any>(null);
function onSeedanceSubmitted() {
  seedanceResultRef.value?.refreshFeed?.();
}
</script>

<template>
  <div class="editor-shell" :class="{ 'sd-noscroll': tool === 'seedance' }">
    <NewHeader />
    <CreditsDialog />
    <div class="body has-bridge" :class="{ 'fs-swap': tool === 'face-swap', 'sd-fixed': tool === 'seedance' }">
      <!-- 左侧 sidebar -->
      <EditorSidebar :tool="tool" @switch="switchTool" />

      <!-- 中间控制面板（不同 tool 不同组件） -->
      <div class="middle">
        <EditorPanelFaceSwap
          v-if="tool === 'face-swap'"
          ref="activePanel"
          :target-material-id="targetMaterialId"
          :target-media-url="sourceImage"
          :is-uploading="isUploading"
          @result="handleResult"
          @state="panelState = $event"
        />
        <EditorPanelSexPose
          v-else-if="tool === 'sex-pose'"
          ref="activePanel"
          :source-images="sourceImages"
          :is-uploading="isUploading"
          :preset-aspect="uploadedAspect"
          @result="handleResult"
          @state="panelState = $event"
        />
        <EditorPanelAiVideo
          v-else-if="tool === 'ai-video'"
          ref="activePanel"
          :source-image="sourceImage"
          :is-uploading="isUploading"
          @result="handleResult"
          @state="panelState = $event"
        />
        <EditorPanelSeedance
          v-else-if="tool === 'seedance'"
          ref="activePanel"
          :source-image="sourceImage"
          :is-uploading="isUploading"
          @result="handleResult"
          @state="panelState = $event"
          @submitted="onSeedanceSubmitted"
        />

        <!-- face-swap：桌面在面板下方 / 移动端常驻底部。
             seedance：桌面用面板内 sticky 底部按钮（见 EditorPanelSeedance），
                       故此处仅移动端保留固定底栏（md:hidden）。 -->
        <EditorBridgeGenerate
          v-if="tool === 'face-swap' || tool === 'seedance'"
          :class="{ 'md:hidden': tool === 'seedance' }"
          :state="panelState"
          @generate="activePanel?.generate()"
        />
      </div>

      <!-- 右侧 上传 + 结果 + 历史素材 -->
      <div class="right">
        <!-- seedance：走独立结果面板（无上传，素材全部在左侧 panel 内管理） -->
        <SeedanceResultPanel
          v-if="tool === 'seedance'"
          ref="seedanceResultRef"
          @use-example="onUseSeedanceExample"
          @use-video="onUseSeedanceVideo"
        />
        <EditorUploadZone
          v-else
          :tool="tool"
          :result="result"
          :generating="panelState.busy"
          @upload="handleUpload"
          @reset="handleReset"
          @uploading="handleUploading"
        />

        <!-- sex-pose / ai-video：生成按钮放在上传区/结果区下面（移动端常驻底部）；
             seedance 的生成按钮已移到左侧设置面板底部 -->
        <EditorBridgeGenerate
          v-if="tool !== 'face-swap' && tool !== 'seedance'"
          :state="panelState"
          @generate="activePanel?.generate()"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.editor-shell { @apply min-h-screen bg-neutral-950 text-white; }
.body {
  display: grid;
  gap: 1rem;
  padding: 6rem 1rem 2rem;
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 768px) {
  .body {
    grid-template-columns: 200px minmax(420px, 1fr) minmax(0, 1.4fr);
    padding: 6rem 1.5rem 2rem;
    gap: 1.5rem;
    max-width: 1600px;
    margin: 0 auto;
  }
}
.middle, .right { min-width: 0; }

/* seedance 桌面端：整页锁高不滚，设置面板 / 结果面板各自内部滚动。
   仅桌面(md+)生效；移动端保持整页滚动 + 固定底栏。 */
@media (min-width: 768px) {
  .editor-shell.sd-noscroll { height: 100vh; overflow: hidden; }
  .body.sd-fixed {
    height: 100vh;
    min-height: 0;
  }
  .body.sd-fixed .middle {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .body.sd-fixed .right {
    min-height: 0;
    /* 结果面板作为 flex 子项填满整列，与左侧设置面板等高（内部滚动交给卡片） */
    display: flex;
    flex-direction: column;
  }
}

/* 仅 face-swap：上传人脸模块(middle) 与 上传素材模块(right) 互换位置。
   sidebar 默认 order:0 占第一列；right→第二列，middle→第三列。
   移动端单列堆叠也随之交换顺序。其它 tool(sex-pose/ai-video) 不受影响。 */
.body.fs-swap .right { order: 1; }
.body.fs-swap .middle { order: 2; }

/* 桥接生成按钮移动端固定在底部，会遮住内容，给页面留出空间 */
@media (max-width: 767px) {
  .body.has-bridge { padding-bottom: 96px; }
}
</style>
